<template>
  <div
    :class="b({ alwaysExpanded, variant, hasContent: !!$slots.content })"
    :style="dynamicStyle"
    @click="isExpanded = !isExpanded"
  >
    <div :class="b('icon')">
      <!-- @slot Use this slot for the icon/letters -->
      <slot></slot>
    </div>
    <div
      v-if="$slots.content"
      ref="content"
      :class="b('content')"
    >
      <!-- @slot Use this slot for badges additional content -->
      <slot name="content"></slot>
    </div>
  </div>
</template>

<script>
  import useSessionStore from '@/stores/session';

  /**
   * Renders a badge that can expand and show additional content.
   */
  export default {
    name: 'e-badge',

    // mixins: [],

    props: {
      /**
       * Always expanded option.
       * Disables expand/contract functionality.
       */
      alwaysExpanded: {
        type: Boolean,
        default: false,
      },

      /**
       * Styling variant of the badge.
       */
      variant: {
        type: String,
        default: 'default',
        validator(value) {
          return ['default', 'gradient', 'local-points'].includes(value);
        },
      },
    },
    setup() {
      return {
        sessionStore: useSessionStore(),
      };
    },
    data() {
      return {
        /**
         * @type {Boolean} - The current expanded state
         */
        isExpanded: false,
      };
    },

    computed: {
      /**
       * Gets the dynamic inline style values.
       *
       * @returns {Object}
       */
      dynamicStyle() {
        return (
          this.variant === 'local-points' && {
            backgroundColor: this.sessionStore.localPointsColor,
          }
        );
      },
    },
    watch: {
      /**
       * Observes expanded state and triggers expand/contract.
       *
       * @param {Boolean} isExpanded - Expanded state.
       */
      isExpanded(isExpanded) {
        if (isExpanded) {
          this.expand();
        } else {
          this.contract();
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Expands the badge.
       */
      expand() {
        const { content } = this.$refs;

        content.style.width = 'auto';
        const width = content.clientWidth; // Reads the width without being rendered.

        content.style.width = 0;

        setTimeout(() => {
          content.style.width = `${width}px`;
          content.addEventListener('transitionend', this.onExpanded);
        }, 20);
      },

      /**
       * Contracts the badge.
       */
      contract() {
        const { content } = this.$refs;
        const width = content.clientWidth;

        content.removeEventListener('transitionend', this.onExpanded);
        content.style.width = `${width}px`;

        setTimeout(() => {
          content.style.width = '0';
        }, 20);
      },

      /**
       * Resets width after content was expanded.
       */
      onExpanded() {
        const { content } = this.$refs;

        content.removeEventListener('transitionend', this.onExpanded);
        content.style.width = 'auto';
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  // @use '@/setup/scss/extends' as *;

  .e-badge {
    $this: &;

    display: inline-flex;
    justify-content: center;
    align-items: center;
    height: functions.rem(40px);
    padding: functions.rem(8px);
    border-radius: functions.rem(20px);
    background: variables.$color-secondary--3;
    box-shadow: 0 5px 10px 0 rgba(152, 119, 15, 17%);

    &--has-content {
      cursor: pointer;
    }

    &__icon {
      @include mixins.font(variables.$font-size--12);

      display: flex;
      justify-content: center;
      align-items: center;
      width: functions.rem(24px);
      height: functions.rem(24px);
      overflow: hidden;
      border: 1px solid variables.$color-grayscale--1000;
      border-radius: 50%;
      color: variables.$color-grayscale--1000;

      svg {
        width: functions.rem(15px);
        height: functions.rem(15px);
      }

      svg * {
        fill: currentColor;
      }
    }

    &__content {
      @include mixins.font(variables.$font-size--14);

      width: 0;
      overflow: hidden;
      color: variables.$color-grayscale--1000;
      white-space: nowrap;
      transition: width variables.$transition-duration--300 ease-in-out;

      &::before, // before/after was used instead of padding because open/close transition.
      &::after {
        display: inline-block;
        content: '';
        width: functions.rem(variables.$spacing--5);
      }

      &::after {
        width: functions.rem(variables.$spacing--2);
      }
    }

    &--always-expanded {
      cursor: default;
      pointer-events: none;

      #{$this}__content {
        width: auto;
      }
    }

    &--variant-gradient {
      background: linear-gradient(45deg, variables.$color-gradient--1);
    }
  }
</style>
